import { format, formatDistanceToNow, isToday, isYesterday, isThisYear } from 'date-fns';
import { DATE_FIELD_SET } from './inspector-schema.js';

// Check if a key is a date field
export function isDateKey(key) {
  const normalized = (key || '').toLowerCase();
  if (DATE_FIELD_SET.has(normalized)) {
    return true;
  }
  const lastPart = normalized.split('.').pop();
  return DATE_FIELD_SET.has(lastPart) || normalized.endsWith('_at') || normalized.endsWith('_date');
}

// Format a timestamp to a succinct readable string using 24-hour time with seconds
export function formatDateTime(timestamp) {
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return String(timestamp);
    }

    // Today: just show time (e.g., "15:45:23")
    if (isToday(date)) {
      return format(date, 'HH:mm:ss');
    }

    // Yesterday: "Yesterday 15:45:23"
    if (isYesterday(date)) {
      return format(date, "'Yesterday' HH:mm:ss");
    }

    // This year: "Jan 15, 15:45:23"
    if (isThisYear(date)) {
      return format(date, 'MMM d, HH:mm:ss');
    }

    // Older: "Jan 15 2024, 15:45:23"
    return format(date, 'MMM d yyyy, HH:mm:ss');
  } catch {
    return String(timestamp);
  }
}

// Format a number with appropriate precision
export function formatNumber(value) {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return String(value);
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  if (Number.isInteger(num)) {
    return num.toLocaleString();
  }
  if (Math.abs(num) < 0.01 && num !== 0) {
    return num.toExponential(2);
  }
  if (Math.abs(num) > 1000000) {
    return num.toExponential(2);
  }
  const str = num.toFixed(2);
  return parseFloat(str).toString();
}

// Convert a value to a number if possible
export function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

// Convert a field name to a human-readable label
export function toLabel(field) {
  const cleaned = field
    .replace(/\[\]/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' → ');
  return cleaned
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Wrap long text values for display
export function wrapText(value, maxLength = 60) {
  const str = String(value || '');
  if (str.length <= maxLength) {
    return str;
  }
  return str.substring(0, maxLength) + '…';
}

// Format a value for display in the Inspector
export function formatInspectorValue(key, value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (isDateKey(key)) {
    if (typeof value === 'number') {
      return formatDateTime(value);
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return formatDateTime(parsed);
    }
  }
  if (typeof value === 'boolean') {
    return value ? '✓' : '✗';
  }
  if (typeof value === 'number') {
    return formatNumber(value);
  }
  if (typeof value === 'string') {
    if (value.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return value.split('-')[0] + '…';
    }
    if (value.match(/^https?:\/\//)) {
      try {
        const url = new URL(value);
        return url.hostname + (url.pathname === '/' ? '' : url.pathname);
      } catch {
        return wrapText(value);
      }
    }
    return wrapText(value);
  }
  if (Array.isArray(value)) {
    return `Array[${value.length}]`;
  }
  if (typeof value === 'object') {
    return 'Object';
  }
  return String(value);
}

// Check if a value is a plain object
export function isPlainObject(value) {
  return value !== null && typeof value === 'object' && value.constructor === Object;
}

// Extract validation information from an event
export function extractValidation(raw) {
  if (raw.errors && raw.errors.length > 0) {
    return { type: 'error', message: `Validation errors: ${raw.errors.join(', ')}` };
  }
  if (raw.warnings && raw.warnings.length > 0) {
    return { type: 'warning', message: `Warnings: ${raw.warnings.join(', ')}` };
  }
  if (raw.info && raw.info.length > 0) {
    return { type: 'info', message: raw.info.join(', ') };
  }
  return null;
}

// Extract available fields from buffered events
export function extractFieldsFromEvents(events) {
  const discoveredFields = new Set();
  const fieldValues = {};

  function traverse(obj, path = '') {
    if (!obj || typeof obj !== 'object') return;

    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const fieldPath = path ? `${path}.${key}` : key;

      if (value === null || value === undefined) return;

      if (Array.isArray(value)) {
        const arrayPath = `${fieldPath}[]`;
        discoveredFields.add(arrayPath);

        // Sample first item for structure
        if (value.length > 0 && typeof value[0] === 'object') {
          traverse(value[0], arrayPath.replace('[]', '[0]'));
        } else {
          // Store primitive array values
          if (!fieldValues[arrayPath]) fieldValues[arrayPath] = new Set();
          value.forEach(v => {
            if (typeof v === 'string' || typeof v === 'number') {
              fieldValues[arrayPath].add(v);
            }
          });
        }
      } else if (typeof value === 'object') {
        discoveredFields.add(fieldPath);
        traverse(value, fieldPath);
      } else {
        discoveredFields.add(fieldPath);

        // Store values for autocomplete
        if (!fieldValues[fieldPath]) fieldValues[fieldPath] = new Set();
        if (typeof value === 'string' || typeof value === 'number') {
          fieldValues[fieldPath].add(value);
        }
      }
    });
  }

  events.forEach(event => traverse(event.raw));

  return {
    fields: Array.from(discoveredFields).sort(),
    values: Object.entries(fieldValues).reduce((acc, [field, values]) => {
      acc[field] = Array.from(values).slice(0, 100); // Limit to 100 values per field
      return acc;
    }, {})
  };
}
